import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import {
  Box,
  Button,
  ButtonGroup,
  FilterChip,
  Heading,
  IconButton,
  Input,
  Menu,
  Modal,
  Pagination,
  Paragraph,
  Popover,
  Switch,
  Table,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import {
  IconChevronDownFilled,
  IconDownloadOutline,
  IconExternalOutline,
  IconInfoFilled,
  IconTagDollarSignOutline,
} from '@zillow/constellation-icons';

import { BuildingPhotographyStatus } from '@zg-rentals/ts-rental-manager-api-client';

import { useMediaCreditsEligibility } from '../../../hooks';

import {
  MEDIA_CREDITS_EMPTY_STATE_MESSAGE,
  MEDIA_CREDITS_MODAL_HEADING,
  MEDIA_CREDITS_NO_ARYEO_COVERAGE_MESSAGE,
  MEDIA_CREDITS_NO_ARYEO_COVERAGE_TITLE,
  MEDIA_CREDITS_REDEEM_CTA_TEXT,
  ZRN_TOOLTIP_COPY,
  ZRN_TOOLTIP_TITLE,
} from '../../../constants/MediaCreditsConstants';
import QueryConstants from '../../../constants/QueryConstants';

import type { ClientAdProductType } from '@zg-rentals/ts-rental-manager-api-client';
import type { MediaCreditProperty } from '../../../hooks/useMediaCreditsEligibility';

const PROPERTIES_PER_PAGE = 10;
const ALL_PACKAGES_FILTER = 'all';
const ALL_METROPOLITAN_SERVICE_AREAS_FILTER = 0;
const DENSE_TABLE_CELL_STYLES = {
  paddingInline: '250',
  paddingBlock: '300',
} as const;
const TABLE_TEXT_WRAP_STYLES = {
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
} as const;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

type FilterOption = {
  label: string;
  value: string;
};

type FilterChipWithMenuProps = {
  onChange: (value: string) => void;
  options: Array<FilterOption>;
  selectedValue: string;
  defaultValue: string;
};

const FilterChipWithMenu = ({ onChange, options, selectedValue, defaultValue }: FilterChipWithMenuProps) => {
  const selectedOption = options.find((option) => option.value === selectedValue);

  return (
    <Menu
      content={
        <Box css={{ minWidth: '250px', maxHeight: '300px', overflow: 'auto' }}>
          {options.map((option) => (
            <Menu.Item key={option.value} onClick={() => onChange(option.value)}>
              <Menu.ItemLabel>
                <Text textStyle={selectedValue === option.value ? 'body-bold' : 'body'}>{option.label}</Text>
              </Menu.ItemLabel>
            </Menu.Item>
          ))}
        </Box>
      }
      trigger={
        <FilterChip.Root selected={selectedValue !== defaultValue}>
          <FilterChip.Label>{selectedOption?.label ?? ''}</FilterChip.Label>
          <FilterChip.Icon css={{ marginRight: '0', marginLeft: '200' }}>
            <IconChevronDownFilled />
          </FilterChip.Icon>
        </FilterChip.Root>
      }
    />
  );
};

const renderZrnHeaderCell = () => (
  <Popover
    body={<Paragraph>{ZRN_TOOLTIP_COPY}</Paragraph>}
    trigger={
      <IconButton
        title={ZRN_TOOLTIP_TITLE}
        size="sm"
        emphasis="bare"
        shape="circle"
        css={{ color: 'icon.muted', width: 'icon.sm', height: 'icon.sm', verticalAlign: 'text-top', marginLeft: '100' }}
        icon={<IconInfoFilled />}
        onClick={(event) => event.stopPropagation()}
      />
    }
    header={<Heading level={2}>{ZRN_TOOLTIP_TITLE}</Heading>}
  />
);

const MediaCreditsCheckoutHelper = () => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedPackageName, setSelectedPackageName] = useState<string>(ALL_PACKAGES_FILTER);
  const [selectedMetropolitanServiceAreaRegionId, setSelectedMetropolitanServiceAreaRegionId] = useState(
    ALL_METROPOLITAN_SERVICE_AREAS_FILTER,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [showRedeemed, setShowRedeemed] = useState(false);

  const {
    eligiblePropertyCount,
    filteredProperties,
    isLoading,
    metropolitanServiceAreaOptions,
    packageOptions,
    totalCreditAmount,
  } = useMediaCreditsEligibility(
    searchText,
    selectedPackageName === ALL_PACKAGES_FILTER ? undefined : (selectedPackageName as ClientAdProductType),
    selectedMetropolitanServiceAreaRegionId || undefined,
    showRedeemed,
  );

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / PROPERTIES_PER_PAGE));
  const startIndex = (currentPage - 1) * PROPERTIES_PER_PAGE;
  const paginatedProperties = filteredProperties.slice(startIndex, startIndex + PROPERTIES_PER_PAGE);

  const packageFilterOptions = useMemo(
    () => [{ value: ALL_PACKAGES_FILTER, label: 'All packages' }, ...packageOptions],
    [packageOptions],
  );
  const metropolitanServiceAreaFilterOptions = useMemo(
    () => [
      { value: String(ALL_METROPOLITAN_SERVICE_AREAS_FILTER), label: 'All metropolitan service areas' },
      ...metropolitanServiceAreaOptions.map((option) => ({ value: String(option.value), label: option.label })),
    ],
    [metropolitanServiceAreaOptions],
  );

  const handleMetropolitanServiceAreaChange = (value: string) => {
    setSelectedMetropolitanServiceAreaRegionId(Number(value));
    setCurrentPage(1);
  };

  const hasActiveFilters =
    searchText.length > 0 ||
    selectedPackageName !== ALL_PACKAGES_FILTER ||
    selectedMetropolitanServiceAreaRegionId !== ALL_METROPOLITAN_SERVICE_AREAS_FILTER;

  const clearFilters = () => {
    setSearchText('');
    setSelectedPackageName(ALL_PACKAGES_FILTER);
    setSelectedMetropolitanServiceAreaRegionId(ALL_METROPOLITAN_SERVICE_AREAS_FILTER);
    setCurrentPage(1);
  };

  const downloadCsv = useCallback(() => {
    const escapeCsvField = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const headers = ['Property', 'Address', 'Credit', 'ZRN', 'Promo Code', 'Redeemed'];
    const rows = filteredProperties.map((property) => [
      escapeCsvField(property.propertyName ?? ''),
      escapeCsvField(property.address ?? ''),
      property.creditAmount != null ? String(property.creditAmount) : '',
      escapeCsvField(property.zrnNumber ?? ''),
      escapeCsvField(
        property.buildingPhotographyStatus === BuildingPhotographyStatus.REDEEMED_VOUCHER
          ? 'Redeemed'
          : property.aryeoCoverage === false
            ? 'N/A'
            : (property.promoCode ?? ''),
      ),
      property.buildingPhotographyStatus === BuildingPhotographyStatus.REDEEMED_VOUCHER ? 'x' : '',
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'media-credits.csv';
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredProperties]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const currentScrollY = window.scrollY;
    const scrollContainers = [
      document.documentElement,
      ...Array.from(
        document.querySelectorAll<HTMLElement>(
          [
            '[data-testid="AppLayoutContainer"]',
            '[data-testid="LeftNavLayoutContainer"]',
            '[data-testid="HeaderNavLayoutContainer"]',
            '[data-testid="BodyContainer"]',
            '[data-testid="BodyContentContainer"]',
          ].join(', '),
        ),
      ),
    ];
    const previousScrollContainerStyles = scrollContainers.map((element) => ({
      element,
      overflow: element.style.overflow,
      overflowY: element.style.overflowY,
      overscrollBehavior: element.style.overscrollBehavior,
    }));
    const previousBodyStyles = {
      overflow: document.body.style.overflow,
      overflowY: document.body.style.overflowY,
      overscrollBehavior: document.body.style.overscrollBehavior,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    const getAllowedScrollContent = (target: EventTarget | null) => {
      if (!(target instanceof Element)) {
        return null;
      }

      let element: Element | null = target;

      while (element) {
        const componentName = element.getAttribute('data-c11n-component');
        const role = element.getAttribute('role');

        if (componentName === 'Modal.Content' || componentName === 'Menu.Content' || role === 'menu') {
          return element;
        }

        element = element.parentElement;
      }

      return null;
    };

    const preventBackgroundScroll = (event: WheelEvent | TouchEvent) => {
      if (getAllowedScrollContent(event.target)) {
        return;
      }
      event.preventDefault();
    };

    document.body.style.overflow = 'hidden';
    document.body.style.overflowY = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${currentScrollY}px`;
    document.body.style.width = '100%';
    scrollContainers.forEach((element) => {
      element.style.overflow = 'hidden';
      element.style.overflowY = 'hidden';
      element.style.overscrollBehavior = 'none';
    });
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false, capture: true });
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false, capture: true });

    return () => {
      previousScrollContainerStyles.forEach(({ element, overflow, overflowY, overscrollBehavior }) => {
        element.style.overflow = overflow;
        element.style.overflowY = overflowY;
        element.style.overscrollBehavior = overscrollBehavior;
      });
      document.body.style.overflow = previousBodyStyles.overflow;
      document.body.style.overflowY = previousBodyStyles.overflowY;
      document.body.style.overscrollBehavior = previousBodyStyles.overscrollBehavior;
      document.body.style.position = previousBodyStyles.position;
      document.body.style.top = previousBodyStyles.top;
      document.body.style.width = previousBodyStyles.width;
      window.scrollTo(0, currentScrollY);
      document.removeEventListener('wheel', preventBackgroundScroll, { capture: true });
      document.removeEventListener('touchmove', preventBackgroundScroll, { capture: true });
    };
  }, [isOpen]);

  useEffect(() => {
    if (router.query[QueryConstants.SHOW_MEDIA_CREDITS_MODAL] !== 'true') {
      return;
    }
    setIsOpen(true);
    const { [QueryConstants.SHOW_MEDIA_CREDITS_MODAL]: _, ...restQuery } = router.query;
    void router.replace({ query: restQuery }, undefined, { shallow: true });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [router.isReady, router.query[QueryConstants.SHOW_MEDIA_CREDITS_MODAL]]);

  if (isLoading || eligiblePropertyCount === 0) {
    return null;
  }

  const renderPropertyRow = (property: MediaCreditProperty) => {
    const isRedeemed = property.buildingPhotographyStatus === BuildingPhotographyStatus.REDEEMED_VOUCHER;

    return (
      <Table.Row key={property.listingAlias}>
        <Table.Cell css={DENSE_TABLE_CELL_STYLES}>
          <Text textStyle="body-bold" css={TABLE_TEXT_WRAP_STYLES}>
            {property.propertyName}
          </Text>
        </Table.Cell>
        <Table.Cell css={DENSE_TABLE_CELL_STYLES}>
          <Text textStyle="body-sm" color="text.subtle" css={TABLE_TEXT_WRAP_STYLES}>
            {property.address}
          </Text>
        </Table.Cell>
        <Table.Cell css={{ ...DENSE_TABLE_CELL_STYLES, whiteSpace: 'nowrap' }}>
          {property.creditAmount != null ? currencyFormatter.format(property.creditAmount) : null}
        </Table.Cell>
        <Table.Cell css={{ ...DENSE_TABLE_CELL_STYLES, whiteSpace: 'nowrap' }}>
          <Text css={TABLE_TEXT_WRAP_STYLES}>{property.zrnNumber}</Text>
        </Table.Cell>
        <Table.Cell css={DENSE_TABLE_CELL_STYLES}>
          <Box css={{ display: 'inline-flex', alignItems: 'center', gap: '200' }}>
            {isRedeemed ? (
              <Popover
                header={<Heading level={3}>Redeemed</Heading>}
                body={<Paragraph>This voucher has been redeemed.</Paragraph>}
                trigger={
                  <Tag tone="success" size="sm" css={{ cursor: 'pointer' }}>
                    Redeemed
                  </Tag>
                }
              />
            ) : (
              <>
                <Text css={TABLE_TEXT_WRAP_STYLES}>
                  {property.aryeoCoverage === false ? 'N/A' : (property.promoCode ?? '')}
                </Text>
                {property.aryeoCoverage === false ? (
                  <Popover
                    header={<Heading level={3}>{MEDIA_CREDITS_NO_ARYEO_COVERAGE_TITLE}</Heading>}
                    body={<Paragraph>{MEDIA_CREDITS_NO_ARYEO_COVERAGE_MESSAGE}</Paragraph>}
                    trigger={
                      <IconButton
                        title={MEDIA_CREDITS_NO_ARYEO_COVERAGE_TITLE}
                        size="sm"
                        emphasis="bare"
                        shape="circle"
                        css={{ color: 'icon.muted' }}
                        icon={<IconInfoFilled />}
                      />
                    }
                  />
                ) : (
                  property.orderFormUrl && (
                    <Button size="sm" tone="brand" emphasis="outlined" icon={<IconExternalOutline />} asChild>
                      <a
                        href={property.orderFormUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Redeem media credit for ${property.propertyName}`}
                      >
                        Redeem
                      </a>
                    </Button>
                  )
                )}
              </>
            )}
          </Box>
        </Table.Cell>
      </Table.Row>
    );
  };

  return (
    <>
      <Box css={{ display: 'flex', justifyContent: 'flex-start' }}>
        <TextButton icon={<IconTagDollarSignOutline />} onClick={() => setIsOpen(true)}>
          Redeem {currencyFormatter.format(totalCreditAmount)} in media credits
        </TextButton>
      </Box>
      <Modal
        open={isOpen}
        onOpenChange={setIsOpen}
        size={{ base: 'fullScreen', md: 'xl' }}
        css={{ overscrollBehavior: 'contain' }}
        header={<Heading level={2}>{MEDIA_CREDITS_MODAL_HEADING}</Heading>}
        body={
          <Box css={{ display: 'flex', flexDirection: 'column', gap: '600', maxWidth: '100%', overflowX: 'hidden' }}>
            <Paragraph color="text.subtle">
              Redeem media credits to apply them toward photos, virtual tours, or other services from Zillow Media
              Experts.
            </Paragraph>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '500' }}>
              <Input
                aria-label="Search eligible properties"
                placeholder="Search by property, address, or ZRN"
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value);
                  setCurrentPage(1);
                }}
              />
              <Box css={{ display: 'flex', flexWrap: 'wrap', gap: '300' }}>
                <FilterChipWithMenu
                  selectedValue={selectedPackageName}
                  defaultValue={ALL_PACKAGES_FILTER}
                  options={packageFilterOptions}
                  onChange={(value) => {
                    setSelectedPackageName(value);
                    setCurrentPage(1);
                  }}
                />
                {metropolitanServiceAreaOptions.length > 0 && (
                  <FilterChipWithMenu
                    selectedValue={String(selectedMetropolitanServiceAreaRegionId)}
                    defaultValue={String(ALL_METROPOLITAN_SERVICE_AREAS_FILTER)}
                    options={metropolitanServiceAreaFilterOptions}
                    onChange={handleMetropolitanServiceAreaChange}
                  />
                )}
                {hasActiveFilters && <TextButton onClick={clearFilters}>Clear filters</TextButton>}
                <Box css={{ display: 'flex', alignItems: 'center', gap: '200', marginLeft: 'auto' }}>
                  <Switch
                    aria-label="Show redeemed vouchers"
                    size="md"
                    checked={showRedeemed}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowRedeemed(event.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                  <Text textStyle="body-sm">Show redeemed</Text>
                </Box>
              </Box>
            </Box>

            {filteredProperties.length === 0 && <Paragraph>{MEDIA_CREDITS_EMPTY_STATE_MESSAGE}</Paragraph>}

            {filteredProperties.length > 0 && (
              <Box css={{ maxWidth: '100%', minWidth: 0, overflowX: 'hidden', width: '100%' }}>
                <Table.Root
                  aria-label="Eligible properties with unused media credits"
                  appearance="horizontal"
                  size="sm"
                  css={{ maxWidth: '100%', width: '100%', tableLayout: 'fixed' }}
                >
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell css={{ ...DENSE_TABLE_CELL_STYLES, width: '25%' }}>Property</Table.HeaderCell>
                      <Table.HeaderCell css={{ ...DENSE_TABLE_CELL_STYLES, width: '37%' }}>Address</Table.HeaderCell>
                      <Table.HeaderCell css={{ ...DENSE_TABLE_CELL_STYLES, width: '5%' }}>Credit</Table.HeaderCell>
                      <Table.HeaderCell css={{ ...DENSE_TABLE_CELL_STYLES, width: '15%' }}>
                        <Box css={{ display: 'inline-flex', alignItems: 'center' }}>
                          ZRN
                          {renderZrnHeaderCell()}
                        </Box>
                      </Table.HeaderCell>
                      <Table.HeaderCell css={{ ...DENSE_TABLE_CELL_STYLES, width: '18%' }}>Promo code</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>{paginatedProperties.map(renderPropertyRow)}</Table.Body>
                </Table.Root>
                <Box
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '400',
                    padding: '400',
                  }}
                >
                  <Text>
                    Showing {startIndex + 1}–
                    {Math.min(startIndex + paginatedProperties.length, filteredProperties.length)} of{' '}
                    {filteredProperties.length}
                  </Text>
                  {totalPages > 1 && (
                    <Pagination
                      aria-label="Eligible properties pagination"
                      css={{ marginInline: 0, width: 'auto' }}
                      currentPageIndex={currentPage - 1}
                      onPageSelected={(pageIndex) => setCurrentPage(pageIndex + 1)}
                      showNumberButtons={{ base: false, md: true }}
                      totalPages={totalPages}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        }
        footer={
          <ButtonGroup aria-label="Media credits modal controls">
            <Button onClick={() => setIsOpen(false)}>Close</Button>
            <Button
              emphasis="outlined"
              icon={<IconDownloadOutline />}
              onClick={downloadCsv}
              disabled={filteredProperties.length === 0}
            >
              Download CSV
            </Button>
            <Button tone="brand" emphasis="filled" asChild>
              <a
                href="https://media.zillowmediaexperts.com/order-forms/01909ec8-d42c-713a-9727-8eb553387fd1"
                target="_blank"
                rel="noopener noreferrer"
              >
                {MEDIA_CREDITS_REDEEM_CTA_TEXT}
              </a>
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
};

export default MediaCreditsCheckoutHelper;
